import { DateTime } from 'luxon';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SalesOrder, SalesOrderStatus } from '../../../interfaces/customer-api';
import Triangle from '../../assets/arrow-dropdown.svg';
import OrderDeliveredIcon from '../../assets/order-status-icons/order-delivered.svg';
import OrderInWareHouseProcessIcon from '../../assets/order-status-icons/order-in-warehouse-process.svg';
import OrderNotConfirmedIcon from '../../assets/order-status-icons/order-not-confirmed.svg';
import OrderOpenIcon from '../../assets/order-status-icons/order-open.svg';
import OrderShippedIcon from '../../assets/order-status-icons/order-shipped.svg';
import { useOvermindState } from '../../state';
import { cns, formatDate, mkEnv } from '../../utils';
import { Tooltip } from '../Tooltip/Tooltip';
import css from './MyPartsWidget.module.scss';

const getStatusIcon = (status: SalesOrderStatus) => {
    switch (status) {
        case SalesOrderStatus.NotConfirmed:
            return <OrderNotConfirmedIcon />;
        case SalesOrderStatus.Open:
            return <OrderOpenIcon />;
        case SalesOrderStatus.InWarehouseProcess:
            return <OrderInWareHouseProcessIcon />;
        case SalesOrderStatus.Shipped:
            return <OrderShippedIcon />;
        case SalesOrderStatus.Delivered:
            return <OrderDeliveredIcon />;
        default:
            return null;
    }
};

const getStatusTranslationKey = (status: SalesOrderStatus) => {
    switch (status) {
        case SalesOrderStatus.NotConfirmed:
            return 'Processing';
        case SalesOrderStatus.Open:
            return 'Order received';
        case SalesOrderStatus.InWarehouseProcess:
            return 'At warehouse';
        case SalesOrderStatus.Shipped:
            return 'Shipped';
        case SalesOrderStatus.Delivered:
            return 'Delivered';
        default:
            return '';
    }
};

const OrderStatus: React.FC<{ salesOrder: SalesOrder }> = ({ salesOrder }: { salesOrder: SalesOrder }) => {
    const { t } = useTranslation('MyPartsWidget');
    // Render statuses in order but ignore cancelled
    const statusProgress: SalesOrderStatus[] = Object.values(SalesOrderStatus).filter(
        (status) => status !== SalesOrderStatus.Cancelled,
    );
    const currentStatusIndex = statusProgress.findIndex((status) => status === salesOrder.status);

    return (
        <div className={css.orderStatus}>
            {statusProgress.map((status, index) => {
                const isLast = index === statusProgress.length - 1;
                const statusText = t(getStatusTranslationKey(status));
                const style = cns(
                    css.orderStatusStep,
                    index < currentStatusIndex && css.doneStep,
                    index === currentStatusIndex && css.activeStep,
                );
                return (
                    <Fragment key={status}>
                        <Tooltip
                            title={statusText}
                            PopperProps={{
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, -16],
                                        },
                                    },
                                ],
                            }}
                            placement="bottom"
                        >
                            <div className={style}>
                                <div className={css.orderStatusIcon}>{getStatusIcon(status)}</div>
                                <p className={css.orderStatusText}>{statusText}</p>
                            </div>
                        </Tooltip>
                        {!isLast && <div className={css.orderStatusLine} />}
                    </Fragment>
                );
            })}
        </div>
    );
};

interface OrderProps {
    order: SalesOrder;
    canNavigateToMyParts: boolean;
}

const Order: React.FC<OrderProps> = ({ order, canNavigateToMyParts }: OrderProps) => {
    const { t } = useTranslation('MyPartsWidget');
    const date = DateTime.fromFormat(order.documentDate, 'yyyy-MM-dd');
    const dateString = date.isValid ? formatDate(date) : order.documentDate;

    const [isOpen, setIsOpen] = useState(false);

    const myPartsOrderURL = `${mkEnv.myPartsURL}/orders;go=${encodeURIComponent(order.globalOrderNumber)}`;

    const hasMultipleShipments = order.rows.length > 1;

    const getShipmentText = (
        status: string | null | undefined,
        trackingUrl: string | null | undefined,
        index: number,
        estimatedDate: string | null | undefined,
        actualDate: string | null | undefined,
    ) => {

        const ShipmentLabel = ({ labelNumber }: { labelNumber: number }) => {
            return (
                <span>
                    {t('Shipment')} {labelNumber}:{' '}
                </span>
            );
        };

        let message = t('Tracking information not available at the moment');

        if (status) {
            if (
                [
                    SalesOrderStatus.NotConfirmed,
                    SalesOrderStatus.Open,
                    SalesOrderStatus.InWarehouseProcess,
                    SalesOrderStatus.Cancelled,
                ].includes(status as SalesOrderStatus)
            ) {
                message = t('Please wait for tracking information...');
                return (
                    <>
                        <ShipmentLabel labelNumber={index + 1} />
                        {message}
                    </>
                );
            }

            if (status === SalesOrderStatus.Shipped && estimatedDate) {
                message = `${t('Est. Delivery')} ${formatDate(DateTime.fromISO(estimatedDate))}`;
                return (
                    <>
                        <ShipmentLabel labelNumber={index + 1} />
                        {trackingUrl ? (
                            <a className={css.link} href={trackingUrl} target="_blank" rel="noopener noreferrer">
                                {message}
                            </a>
                        ) : (
                            message
                        )}
                    </>
                );
            }

            if (status === SalesOrderStatus.Delivered && actualDate) {
                message = `${t('Delivered')} ${formatDate(DateTime.fromISO(actualDate))}`;
                return (
                    <>
                        <ShipmentLabel labelNumber={index + 1} />
                        {trackingUrl ? (
                            <a className={css.link} href={trackingUrl} target="_blank" rel="noopener noreferrer">
                                {message}
                            </a>
                        ) : (
                            message
                        )}
                    </>
                );
            }
        }

        return (
            <>
                <ShipmentLabel labelNumber={index + 1} />
                {message}
            </>
        );
    };
    return (
        <li className={css.order} onClick={() => setIsOpen((prev) => !prev)}>
            <div className={css.customerPoAndDateRow}>
                <h3 className={css.customerPo}>
                    {t('Customer PO')}: <strong>{order.purchaseOrderNumber || t('N/A')}</strong>
                </h3>
                <div className={css.orderDate}>{dateString}</div>
            </div>
            <div className={css.orderRow}>
                <span className={css.label}>{t('Order number')}: </span>
                {canNavigateToMyParts ? (
                    <a href={myPartsOrderURL} className={css.link}>
                        {order.globalOrderNumber}
                    </a>
                ) : (
                    order.globalOrderNumber
                )}
                <div className={css.accordionRow}>
                    {isOpen ? t('Hide tracking') : t('View tracking')}
                    <Triangle className={cns(css.arrow, isOpen && css.arrowOpen)} />
                </div>
            </div>
            <div className={cns(css.tracking, isOpen && css.trackingOpen)} onClick={(e) => e.stopPropagation()}>
                <div className={css.trackingContent}>
                    <h5>{t('Tracking')}</h5>
                    {hasMultipleShipments && <p>{t('Note: this order will be delivered in multiple shipments.')}</p>}
                    <ul className={css.trackingLinks}>
                        {order.rows.map(
                            ({ status, trackingUrl, estimatedDeliveryDate, proofOfDeliveryDate }, index) => (
                                <li key={status}>
                                    {getShipmentText(
                                        status,
                                        trackingUrl,
                                        index,
                                        estimatedDeliveryDate,
                                        proofOfDeliveryDate,
                                    )}
                                </li>
                            ),
                        )}
                    </ul>
                </div>
            </div>
            <OrderStatus salesOrder={order} />
        </li>
    );
};

export const MyPartsWidget: React.FC = () => {
    const translationResponse = useTranslation('MyPartsWidget');
    const { salesOrders, canNavigateToMyParts } = useOvermindState();
    const { t } = translationResponse;

    return (
        <div className={css.myPartsWidget}>
            {salesOrders.length > 0 ? (
                <ul className={css.orders}>
                    {salesOrders.map((order) => (
                        <Order
                            order={order}
                            canNavigateToMyParts={canNavigateToMyParts}
                            key={`order-${order.purchaseOrderNumber}`}
                        />
                    ))}
                </ul>
            ) : (
                <p>{t('No orders')}</p>
            )}
        </div>
    );
};
